import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PUBLIC_URL, AUTH_URL, PRODUCT_ID } from "./../../const";
import { useAuth } from "./../../AuthContext";
import LanguageSwitcher from "../core/switch-language";

const translations = {
    en: {
        title: "Forgot your password?",
        enterEmailNote: "Please enter your email to receive a password reset code.",
        sendCode: "Send Reset Code",
        sending: "Sending...",
        step2Note: "Enter the reset code and your new password.",
        newPassword: "New Password",
        show: "Show",
        hide: "Hide",
        reset: "Reset Password",
        resetting: "Resetting...",
        back: "Back to Login",
        emailPlaceholder: "Email",
        codeSent: "A reset code has been sent to your email.",
        resetSuccess: "Your password has been successfully changed.",
        sendFail: "Failed to send reset code.",
        resetFail: "Failed to reset password.",
    },
    ru: {
        title: "Забыли пароль?",
        enterEmailNote: "Введите email, чтобы получить код для сброса пароля.",
        sendCode: "Отправить код",
        sending: "Отправка...",
        step2Note: "Введите код и новый пароль.",
        newPassword: "Новый пароль",
        show: "Показать",
        hide: "Скрыть",
        reset: "Сбросить пароль",
        resetting: "Сбрасываем...",
        back: "Назад ко входу",
        emailPlaceholder: "Email",
        codeSent: "Код для сброса отправлен на ваш email.",
        resetSuccess: "Пароль успешно изменён.",
        sendFail: "Не удалось отправить код.",
        resetFail: "Не удалось сбросить пароль.",
    },
    ua: {
        title: "Забули пароль?",
        enterEmailNote: "Введіть email, щоб отримати код для скидання пароля.",
        sendCode: "Надіслати код",
        sending: "Надсилання...",
        step2Note: "Введіть код і новий пароль.",
        newPassword: "Новий пароль",
        show: "Показати",
        hide: "Приховати",
        reset: "Скинути пароль",
        resetting: "Скидаємо...",
        back: "Повернутися до входу",
        emailPlaceholder: "Email",
        codeSent: "Код для скидання надіслано на ваш email.",
        resetSuccess: "Пароль успішно змінено.",
        sendFail: "Не вдалося надіслати код.",
        resetFail: "Не вдалося скинути пароль.",
    },
};

const getInitialLang = () =>
    (localStorage.getItem("lang") || "").toLowerCase() || "en";

const ForgotPassword = () => {
    const { updateUserData } = useAuth();
    const navigate = useNavigate();
    const inputs = useRef([]);
    const [lang, setLang] = useState(getInitialLang());
    const t = useMemo(() => translations[lang] || translations.en, [lang]);

    const [email, setEmail] = useState("");
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const [newPassword, setNewPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [step, setStep] = useState(1);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        document.documentElement.lang = lang;
        localStorage.setItem("lang", lang);
    }, [lang]);

    const handleCodeChange = (index, e) => {
        const value = e.target.value;
        if (/^\d*$/.test(value)) {
            const newCode = [...code];
            newCode[index] = value.slice(-1);
            setCode(newCode);
            if (value && index < 5) {
                inputs.current[index + 1]?.focus();
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text").slice(0, 6);
        if (/^\d{6}$/.test(pasteData)) {
            setCode(pasteData.split(""));
            inputs.current[5]?.focus();
        }
    };

    const requestResetCode = async () => {
        setError("");
        setSuccess("");
        setLoading(true);
        try {
            const response = await axios.post(
                `${AUTH_URL}forgot-password`,
                { email, product_id: PRODUCT_ID, lang },
                { headers: { "Accept-Language": lang } }
            );
            updateUserData(response.data);
            setSuccess(t.codeSent);
            setStep(2);
        } catch (err) {
            setError(err.response?.data?.message || t.sendFail);
        } finally {
            setLoading(false);
        }
    };

    const resetPassword = async () => {
        setError("");
        setSuccess("");
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            await axios.post(
                `${AUTH_URL}reset-password`,
                { code, password: newPassword, lang },
                { headers: { Authorization: token, "Accept-Language": lang } }
            );
            setSuccess(t.resetSuccess);
            setTimeout(() => navigate("/sign-in"), 2000);
        } catch (err) {
            setError(err.response?.data?.message || t.resetFail);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div id="main-wrapper">
            <div className="position-relative overflow-hidden radial-gradient min-vh-100 w-100">
                <div className="position-relative z-index-5">
                    <div className="row">
                        <div className="col-xl-7 col-xxl-8">
                            <a href="https://my-sites-guard.com/" className="text-nowrap logo-img d-block px-4 py-9 w-100">
                                <img
                                    src={`https://my-sites-guard.com/assets/img/home_logo.png`}
                                    className="dark-logo"
                                    alt="Logo-Dark"
                                    style={{ maxHeight: "40px" }}
                                />
                            </a>
                            <div className="d-none d-xl-flex align-items-center justify-content-center h-n80">
                                <img
                                    src={`${PUBLIC_URL}/assets/images/backgrounds/login-bg.png`}
                                    alt=""
                                    className="img-fluid"
                                    width="500"
                                />
                            </div>
                        </div>

                        <div className="col-xl-5 col-xxl-4">
                            <div className="card mb-0 shadow-none rounded-0 min-vh-100 h-100">
                                <div className="auth-max-width mx-auto d-flex align-items-center w-100 h-100">
                                    <div className="card-body">
                                        {/* переключатель языка */}
                                        <div className="d-flex justify-content-end mb-3">
                                            <LanguageSwitcher lang={lang} setLang={setLang} />
                                        </div>

                                        <h2 className="fw-bolder fs-7 mb-3">{t.title}</h2>

                                        {error && <div className="alert alert-danger">{error}</div>}
                                        {success && <div className="alert alert-success">{success}</div>}

                                        {step === 1 ? (
                                            <>
                                                <p>{t.enterEmailNote}</p>
                                                <input
                                                    type="email"
                                                    className="form-control mb-3"
                                                    placeholder={t.emailPlaceholder}
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    disabled={loading}
                                                />
                                                <button className="btn btn-primary w-100" onClick={requestResetCode} disabled={loading}>
                                                    {loading ? t.sending : t.sendCode}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <p>{t.step2Note}</p>

                                                <div className="d-flex align-items-center gap-2 gap-sm-3">
                                                    {code.map((value, index) => (
                                                        <input
                                                            key={index}
                                                            type="text"
                                                            className="form-control text-center"
                                                            value={value}
                                                            onChange={(e) => handleCodeChange(index, e)}
                                                            onPaste={index === 0 ? handlePaste : undefined}
                                                            maxLength="1"
                                                            ref={(el) => (inputs.current[index] = el)}
                                                            disabled={loading}
                                                        />
                                                    ))}
                                                </div>

                                                <div className="input-group mt-3 mb-3">
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        className="form-control"
                                                        placeholder={t.newPassword}
                                                        value={newPassword}
                                                        onChange={(e) => setNewPassword(e.target.value)}
                                                        disabled={loading}
                                                    />
                                                    <button
                                                        className="btn btn-outline-secondary"
                                                        type="button"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        disabled={loading}
                                                    >
                                                        {showPassword ? t.hide : t.show}
                                                    </button>
                                                </div>

                                                <button className="btn btn-primary w-100" onClick={resetPassword} disabled={loading}>
                                                    {loading ? t.resetting : t.reset}
                                                </button>
                                            </>
                                        )}

                                        <a href="/sign-in/" className="btn bg-primary-subtle text-primary w-100 mt-3">
                                            {t.back}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* /right */}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
